import dynamic from 'next/dynamic';
import { useState, useRef } from 'react';
import { func, string, object, number } from 'prop-types';
import { Col, Slider as InputSlider } from 'antd';

import { useProductSheet, useSunnei } from 'context/product-sheet';
import { SunneiPicker } from 'components/sunnei/sunneiPicker';
import styles from './style.module.css';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { orderMobileImages } from 'utils/product';

const Slider = dynamic(() => import('react-slick'));
const ProgressiveBlurredImg = dynamic(() => import('components/base/image'));
const Mobile3D = dynamic(() => import('components/viewer-3D/mobile3D').then((mod) => mod.Mobile3D), {
  ssr: false,
});
const ModalZoomImg = dynamic(() => import('components/modalZoomImg').then((mod) => mod.ModalZoomImg));

function Image({ imgSrc, alt, grid, onClick, index, target }) {
  const { isVideo } = imgSrc;

  return (
    <Col className={styles.imagesGrid} onClick={onClick ? () => onClick(imgSrc, index) : null} {...grid}>
      {isVideo ?
        <video
          className="lazy"
          // autoPlay
          loop
          title={`Video of ${alt}`}
          muted
          playsInline
          poster={imgSrc?.poster}
          dangerouslySetInnerHTML={{ __html: imgSrc?.sources }}
        />
      : <ProgressiveBlurredImg
          lowQualitySrc={imgSrc.xs}
          highQualitySrc={imgSrc}
          alt={alt}
          sizes="(min-width: 992px) 33vw, (min-width: 768px) 70vw, 50vw"
          index={index}
          initLoaded={true}
          target={target}
        />
      }
    </Col>
  );
}

Image.propTypes = {
  imgSrc: object,
  grid: object,
  alt: string.isRequired,
  onClick: func,
  index: number,
};

const grid = {
  xs: { span: 12 },
  sm: { span: 12 },
  md: { span: 24 },
  lg: { span: 12 },
  xl: { span: 12 },
  xxl: { span: 12 },
};

export function ImagesGrid() {
  const { productSheet } = useProductSheet();
  const { hasSunneiConfig } = useSunnei();
  const [slideIndex, setSlideIndex] = useState(0);
  const [changeSlideIndex, setChangeSlideIndex] = useState(0);
  const [zoomImg, setZoomImg] = useState(null);
  const sliderRef = useRef();

  // estado derivado
  const defaultAlt = `${productSheet.code} - ${productSheet.brand}`;
  let images = productSheet.images ?? [];
  let galleryImages = productSheet.images ?? [];
  let imagesMobile = productSheet.imagesMobile ?? [];
  const hasMobileImages = Array.isArray(productSheet.imagesMobile) && productSheet.imagesMobile.length > 0;
  if (imagesMobile.length === 0) {
    imagesMobile = images;
  }
  // tener en cuenta sunnei
  if (hasSunneiConfig) {
    const sunneiImages = productSheet?.sunneiInfo?.products?.[productSheet?.sunneiInfo?.shoeOne]?.images;
    const sunneiImagesMobile = productSheet?.sunneiInfo?.products?.[productSheet?.sunneiInfo?.shoeOne]?.imagesMobile;
    images = sunneiImages ?? [];
    imagesMobile = sunneiImagesMobile ?? [];
    if (imagesMobile.length === 0) {
      imagesMobile = images;
    }
    galleryImages = sunneiImages ?? [];
  }

  const zoomGalleryImages = galleryImages.filter((image) => !image?.isVideo);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    swipeToSlide: true,
    easing: false,
    lazyLoad: false,
    draggable: true,
    swipe: true,
    useTransform: false,
    arrows: false,
    beforeChange: (current, next) => setSlideIndex(next),
    afterChange: (current) => setChangeSlideIndex(current),
  };

  const handleClickImage = (imageSrc) => {
    const foundIndex = zoomGalleryImages.findIndex((item) => item.full === imageSrc.full);
    if (foundIndex !== -1) {
      setZoomImg(foundIndex);
      dataLayerHandleEvent({
        event: 'item_photos',
        event_category: 'product detail',
        event_detail_1: 'view',
        event_detail_2: imageSrc?.md,
      });
    }
  };

  const handleCloseZoom = () => {
    setZoomImg(null);
  };

  /**
   * Antes se calculaba en servidor el orden de las imagenes si se detectaba un dispositivo movil. El problema era que se cacheaba y generaba problemas en el que el orden no es el correcto en la vista desktop o movil. Aparte veiamos un problema de hidratación de react.
   * Podemos reordernar las imágenes en el componente porque sabemos que el carousel solo se va a mostrar en movil y se esconde en desktop.
   * Ahora sólo hora se utiliza el normalizeProduct aquí.
   */
  const ImageComponents = orderMobileImages(imagesMobile).reduce((acc, imgSrc, index) => {
    if (index === 0 && hasSunneiConfig) {
      acc.push(<SunneiPicker key="sunneiPicker" />);
    }
    if (index === 1 && productSheet.viewer3d) {
      acc.push(<Mobile3D key="mobileThreeD" code={productSheet.code} mobile={true} />);
    }

    acc.push(
      <Image
        key={`ig-${index}`}
        onClick={slideIndex === changeSlideIndex ? () => handleClickImage(imgSrc) : null}
        grid={grid}
        imgSrc={imgSrc}
        alt={imgSrc?.altText || defaultAlt}
        index={index}
        loading="lazy"
        target={productSheet.target}
      />,
    );
    return acc;
  }, []);

  return (
    <>
      <div key="imageSlider" style={hasMobileImages ? { '--fit': 'contain' } : undefined} className={styles.imageSliderWrapper}>
        <Slider {...settings} className={styles.productsSlider} ref={sliderRef}>
          {ImageComponents}
        </Slider>
        <InputSlider
          className={styles.scrollSlider}
          min={0}
          max={ImageComponents.length}
          value={slideIndex}
          tipFormatter={null}
          handleStyle={{ width: `${100 / ImageComponents.length}%`, transform: 'translateX(0%)' }}
        />
        <p
          className={styles.currentSlideText}
        >{`${ImageComponents.length - (slideIndex + 1) < 1 ? ImageComponents.length : slideIndex + 1} / ${ImageComponents.length}`}</p>
      </div>
      <div key="imageMap" className={`${styles.imageMapWrapper}`}>
        {images.map((imgSrc, index) => (
          <Image
            key={`ig-${index}`}
            onClick={() => handleClickImage(imgSrc, index)}
            grid={grid}
            imgSrc={imgSrc}
            alt={imgSrc?.altText || defaultAlt}
            index={index}
            target={productSheet.target}
          />
        ))}
      </div>
      <ModalZoomImg key={zoomImg} images={zoomGalleryImages} initIndex={zoomImg} handleCloseZoom={handleCloseZoom} />
    </>
  );
}
